// ── IL CONTENUTO DELLA LEZIONE ───────────────────────────────────────
// Cosa si è fatto in campo, o cosa si vuole fare: l'obiettivo, il tipo di
// giocatore che guida il tema, i colpi, le situazioni di gioco e le quattro
// fasi dello schema FITP, ognuna col suo obiettivo e i suoi esercizi.
// È un campo a parte dalle "Note" (`lessonNotes`), che restano quelle
// organizzative e restano nell'evento del calendario: il contenuto
// tecnico nel calendario non ci va.
//
// Il vocabolario è fisso e uguale dappertutto — lezione, note e scheda
// dell'allievo — così un "rovescio" scritto oggi è lo stesso rovescio che
// si cercherà domani. Sulla lezione si salvano gli ID, mai le parole.
//
// Una lezione ricorrente o duplicata il contenuto non lo eredita: ogni
// lezione è diversa. Per questo `addLesson` ed `editLesson` non lo
// scrivono, e lo scrive soltanto `editLessonContent`.

import { normalizeSchema } from "./exerciseSchema.utils"

// I tipi di giocatore della classificazione FITP
export const PLAYER_STYLES = [
	{ id: "attaccanteDaFondo", label: "Attaccante da fondo" },
	{ id: "contrattaccanteDaFondo", label: "Contrattaccante da fondo" },
	{ id: "completo", label: "Completo a tutto campo" },
	{ id: "attaccante", label: "Giocatore d'attacco" },
	{ id: "serveAndVolley", label: "Serve & volley" },
]

export const STROKES = [
	{ id: "dritto", label: "Dritto" },
	{ id: "rovescio", label: "Rovescio" },
	{ id: "servizio", label: "Servizio" },
	{ id: "risposta", label: "Risposta" },
	{ id: "volee", label: "Volée" },
	{ id: "smash", label: "Smash" },
	{ id: "spostamenti", label: "Spostamenti" },
]

export const SITUATIONS = [
	{ id: "servizioERisposta", label: "Servizio e risposta" },
	{ id: "giocoDaFondo", label: "Gioco da fondo" },
	{ id: "attacco", label: "Attacco" },
	{ id: "giocoARete", label: "Gioco a rete" },
	{ id: "difesaEPassante", label: "Difesa e passante" },
]

// Le macroaree: è così che si sceglie su cosa si lavora, nella lezione e
// nella scheda dell'allievo, perché in campo i colpi si allenano insieme.
// Una macroarea raggruppa voci del vocabolario qui sopra: sceglierla le
// accende tutte, e poi se ne può spegnere qualcuna. Sulla lezione restano
// salvate le voci, come prima: la macroarea è accesa se ne ha almeno una,
// e le lezioni già scritte si leggono così come sono.
export const FOCUS_AREAS = [
	{ id: "rimbalzo", label: "Colpi a rimbalzo", list: "strokes", ids: ["dritto", "rovescio"] },
	{ id: "volo", label: "Colpi al volo", list: "strokes", ids: ["volee", "smash"] },
	{
		id: "inizioGioco",
		label: "Colpi di inizio gioco",
		list: "strokes",
		ids: ["servizio", "risposta"],
	},
	{ id: "spostamenti", label: "Spostamenti", list: "strokes", ids: ["spostamenti"] },
	{
		id: "tattica",
		label: "Tattica di gioco",
		list: "situations",
		ids: SITUATIONS.map(situation => situation.id),
	},
]

// Le fasi della lezione secondo lo schema FITP. Ognuna ha un obiettivo —
// il perché, una riga — e i suoi esercizi — cosa si fa e come, uno per
// voce (#178). Separati, così il perché non si perde in mezzo al come, e
// ogni esercizio è una cosa a sé: è quella che il quaderno salverà e
// riprenderà.
export const PHASES = [
	{ id: "riscaldamento", label: "Riscaldamento" },
	{ id: "tecnica", label: "Fase tecnica" },
	{ id: "tattica", label: "Fase tattica" },
	{ id: "defaticamento", label: "Defaticamento" },
]

// Un obiettivo è una riga, un esercizio poche: un piano o un diario, non
// un tema. E una fase non ha bisogno di più di così tanti esercizi.
export const OBJECTIVE_MAX_TEXT = 200
export const EXERCISE_MAX_TEXT = 500
export const MAX_EXERCISES = 20

// A fine lezione, com'è andata (#166): il diario, due righe su tutta la
// lezione, e una riga per qualche allievo, che lui ritrova nelle sue
// lezioni. Stanno nel programma ma non ne hanno bisogno: chi il programma
// non l'ha scritto due note le mette lo stesso.
export const DIARY_MAX_TEXT = 1000
export const PLAYER_NOTE_MAX_TEXT = 300

// Quali colpi e situazioni sono consigliati per ogni tipo di giocatore:
// nel foglio hanno un pallino. La tabella è ancora da scrivere (#164):
// finché un tipo non ha la sua riga, non ha pallini.
export const RECOMMENDED_BY_PLAYER_STYLE = {}

export const recommendedFor = playerStyle => {
	const row = RECOMMENDED_BY_PLAYER_STYLE[playerStyle]
	return {
		strokes: new Set(row?.strokes ?? []),
		situations: new Set(row?.situations ?? []),
	}
}

const labelOf = (list, id) => list.find(item => item.id === id)?.label ?? null

export const playerStyleLabel = id => labelOf(PLAYER_STYLES, id)
export const strokeLabel = id => labelOf(STROKES, id)
export const situationLabel = id => labelOf(SITUATIONS, id)

export const EMPTY_PHASE = { objective: "", exercises: [] }

export const EMPTY_LESSON_CONTENT = {
	objective: "",
	playerStyle: null,
	strokes: [],
	situations: [],
	phases: Object.fromEntries(PHASES.map(phase => [phase.id, EMPTY_PHASE])),
}

// Solo le voci del vocabolario, una volta sola, nell'ordine del
// vocabolario: due maestri che scelgono le stesse cose in un ordine
// diverso salvano la stessa lezione.
const knownIDs = (list, ids) => {
	const chosen = new Set(Array.isArray(ids) ? ids : [])
	return list.filter(item => chosen.has(item.id)).map(item => item.id)
}

const cleanText = (text, max) => (typeof text === "string" ? text.trim().slice(0, max) : "")

// Da dove viene un esercizio preso dal quaderno (src/utils/notebook.utils.js):
// la voce, col suo nome, e il testo com'era quando lo si è preso — è quello
// che fa dire "cambiato". Dello schema basta l'impronta (`schemaKey`, solo
// se ne aveva uno). L'esercizio è copiato: questo serve a dirlo, non a
// rileggerlo. Della voce si tiene anche quale variante (`variantID`): è
// quella che, salvando la lezione cambiata, si può aggiornare.
const normalizeSource = source =>
	typeof source?.entryID === "string" && source.entryID !== ""
		? {
				entryID: source.entryID,
				title: typeof source.title === "string" ? source.title : "",
				text: typeof source.text === "string" ? source.text : "",
				...(typeof source.schemaKey === "string" && source.schemaKey
					? { schemaKey: source.schemaKey }
					: {}),
				...(typeof source.variantID === "string" && source.variantID
					? { variantID: source.variantID }
					: {}),
			}
		: null

const listItem = (list, i) => (Array.isArray(list) ? list[i] : null)

// Gli esercizi senza spazi intorno, e senza quelli rimasti vuoti: nel
// foglio una riga aggiunta e lasciata lì non è un esercizio. Una riga senza
// testo ma con lo schema invece lo è: il disegno dice già cosa si fa.
// `sources` e `schemas`, accanto, dicono per ognuno se viene dal quaderno e
// com'è disegnato, e lo seguono: un esercizio tolto si porta via anche la
// sua provenienza e il suo schema.
const cleanExercises = (exercises, sources = [], schemas = []) =>
	(Array.isArray(exercises) ? exercises : [])
		.map((exercise, i) => ({
			text: cleanText(exercise, EXERCISE_MAX_TEXT),
			source: normalizeSource(listItem(sources, i)),
			schema: normalizeSchema(listItem(schemas, i)),
		}))
		.filter(exercise => exercise.text || exercise.schema)
		.slice(0, MAX_EXERCISES)

// Una fase com'è salvata. Le prime lezioni (#164) avevano un testo solo
// per fase: si legge una riga per esercizio, e l'obiettivo resta da
// scrivere — chi andava a capo per separare gli esercizi li ritrova
// separati. Le provenienze ci sono solo se almeno un esercizio viene dal
// quaderno: una fase scritta a mano resta com'era. Gli schemi uguale: ci
// sono solo se almeno un esercizio ne ha uno.
const normalizePhase = phase => {
	if (typeof phase === "string")
		return {
			objective: "",
			exercises: cleanExercises(phase.split("\n")).map(exercise => exercise.text),
		}
	const exercises = cleanExercises(phase?.exercises, phase?.sources, phase?.schemas)
	return {
		objective: cleanText(phase?.objective, OBJECTIVE_MAX_TEXT),
		exercises: exercises.map(exercise => exercise.text),
		...(exercises.some(exercise => exercise.source)
			? { sources: exercises.map(exercise => exercise.source) }
			: {}),
		...(exercises.some(exercise => exercise.schema)
			? { schemas: exercises.map(exercise => exercise.schema) }
			: {}),
	}
}

// Le righe sugli allievi, per ID: un posto senza nome una riga non ce l'ha.
// Quelle rimaste vuote non ci sono.
const normalizePlayerNotes = notes =>
	Object.fromEntries(
		Object.entries(notes && typeof notes === "object" ? notes : {})
			.filter(([playerID]) => playerID)
			.map(([playerID, text]) => [playerID, cleanText(text, PLAYER_NOTE_MAX_TEXT)])
			.filter(([, text]) => text),
	)

// La lezione del quaderno da cui si è partiti, se si è partiti da una
const normalizeFromNotebook = fromNotebook => {
	const source = normalizeSource(fromNotebook)
	return source
		? {
				entryID: source.entryID,
				title: source.title,
				...(source.variantID ? { variantID: source.variantID } : {}),
			}
		: null
}

// Il contenuto com'è salvato, o com'è nel foglio, ridotto a quello che
// vale: le voci che il vocabolario conosce, i testi senza spazi intorno e
// gli esercizi non vuoti. Quello che manca è vuoto: una lezione di prima
// non ha il campo, e si legge come una lezione senza contenuto.
export const normalizeLessonContent = content => {
	const phases = content?.phases ?? {}
	const fromNotebook = normalizeFromNotebook(content?.fromNotebook)
	const diary = cleanText(content?.diary, DIARY_MAX_TEXT)
	const playerNotes = normalizePlayerNotes(content?.playerNotes)
	return {
		objective: cleanText(content?.objective, OBJECTIVE_MAX_TEXT),
		playerStyle: playerStyleLabel(content?.playerStyle) ? content.playerStyle : null,
		strokes: knownIDs(STROKES, content?.strokes),
		situations: knownIDs(SITUATIONS, content?.situations),
		phases: Object.fromEntries(
			PHASES.map(phase => [phase.id, normalizePhase(phases[phase.id])]),
		),
		...(fromNotebook ? { fromNotebook } : {}),
		// solo se ci sono: un programma senza note resta com'era
		...(diary ? { diary } : {}),
		...(Object.keys(playerNotes).length > 0 ? { playerNotes } : {}),
	}
}

// Una fase è scritta se ha l'obiettivo o almeno un esercizio
export const isPhaseWritten = phase => Boolean(phase?.objective) || phase?.exercises?.length > 0

// Le fasi scritte e quelle ancora da scrivere, nell'ordine dello schema
export const splitPhases = content => {
	const { phases } = normalizeLessonContent(content)
	return {
		written: PHASES.filter(phase => isPhaseWritten(phases[phase.id])),
		missing: PHASES.filter(phase => !isPhaseWritten(phases[phase.id])),
	}
}

// "Fase tattica e defaticamento": le fasi da scrivere in una riga sola
export const joinPhaseLabels = phases => {
	const labels = phases.map(phase => phase.label.toLowerCase())
	const text =
		labels.length > 1
			? `${labels.slice(0, -1).join(", ")} e ${labels[labels.length - 1]}`
			: labels[0] ?? ""
	return text.charAt(0).toUpperCase() + text.slice(1)
}

// Il programma vero e proprio, senza le note di fine lezione: è quello che
// va nel quaderno
export const isProgramEmpty = content => {
	const normalized = normalizeLessonContent(content)
	return (
		!normalized.objective &&
		!normalized.playerStyle &&
		normalized.strokes.length === 0 &&
		normalized.situations.length === 0 &&
		splitPhases(normalized).written.length === 0
	)
}

// Le note di fine lezione: il diario o almeno una riga su un allievo
export const hasLessonNotes = content => {
	const normalized = normalizeLessonContent(content)
	return Boolean(normalized.diary) || Object.keys(normalized.playerNotes ?? {}).length > 0
}

export const isLessonContentEmpty = content =>
	isProgramEmpty(content) && !hasLessonNotes(content)

// La riga di un allievo sotto una lezione, se c'è
export const playerNoteOf = (content, playerID) =>
	(playerID && normalizeLessonContent(content).playerNotes?.[playerID]) || ""

// Accende o spegne una voce di una scelta multipla
export const toggleID = (ids, id) =>
	ids.includes(id) ? ids.filter(other => other !== id) : [...ids, id]

const itemLabel = (area, id) =>
	area.list === "situations" ? situationLabel(id) : strokeLabel(id)

// Le voci di una macroarea, col loro nome
export const areaItems = area => area.ids.map(id => ({ id, label: itemLabel(area, id) }))

// Una macroarea ha un dettaglio da scegliere solo se ha più di una voce
export const hasAreaDetail = area => area.ids.length > 1

export const isAreaOn = (content, area) =>
	area.ids.some(id => (content?.[area.list] ?? []).includes(id))

// Accende una macroarea con tutte le sue voci, o la spegne tutta: torna
// solo la lista che cambia, `{ strokes }` o `{ situations }`
export const toggleArea = (content, area) => {
	const ids = content[area.list] ?? []
	return {
		[area.list]: isAreaOn(content, area)
			? ids.filter(id => !area.ids.includes(id))
			: [...ids, ...area.ids.filter(id => !ids.includes(id))],
	}
}

// Accende o spegne una voce di una macroarea: spenta l'ultima, si spegne
// anche lei
export const toggleAreaItem = (content, area, id) => ({
	[area.list]: toggleID(content[area.list] ?? [], id),
})

// Le macroaree accese, nell'ordine di sempre, con le voci scelte. Il
// dettaglio si scrive solo se non sono tutte: "Colpi a rimbalzo · Rovescio",
// ma "Colpi a rimbalzo" se ci sono dritto e rovescio.
export const chosenAreas = content => {
	const normalized = normalizeLessonContent(content)
	return FOCUS_AREAS.filter(area => isAreaOn(normalized, area)).map(area => {
		const chosen = areaItems(area).filter(item => normalized[area.list].includes(item.id))
		const isWhole = chosen.length === area.ids.length
		return {
			area,
			chosen,
			detail: isWhole ? "" : chosen.map(item => item.label).join(", "),
		}
	})
}

// Su chi si può scrivere una riga: chi era in campo, sostituti compresi, e
// con un ID, perché la riga la ritrova nelle sue lezioni. Chi era assente
// no. Il nome è quello di battesimo, intero solo se due lo condividono.
export const notePlayers = partecipants => {
	const seen = new Set()
	const players = (partecipants ?? []).filter(player => {
		const name = (player?.playerName ?? "").trim()
		if (!player?.playerID || !name || player.isPresent === false || seen.has(player.playerID))
			return false
		seen.add(player.playerID)
		return true
	})
	const firstName = player => player.playerName.trim().split(/\s+/)[0]
	return players.map(player => ({
		playerID: player.playerID,
		name: player.playerName.trim(),
		label:
			players.filter(other => firstName(other) === firstName(player)).length > 1
				? player.playerName.trim()
				: firstName(player),
	}))
}
